import { Alert } from "@package/subframe/components/Alert";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherX } from "@subframe/core";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Acknowledgement copy (NAU-71). The alert description is the exact wording
// signed off in the ticket — do not reword without product sign-off.
const COPY = {
  title: "Acknowledge these failed checks?",
  alertTitle: "What happens when you acknowledge?",
  alertDescription:
    "By choosing to acknowledge these failed checks you are confirming that no further corrective action is required. Acknowledged checks can still be found in the History tab.",
  cancel: "Cancel",
  confirm: "Acknowledge failed checks",
  confirming: "Acknowledging...",
};

interface ConfirmAcknowledgeChecklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklistName: string;
  failedCheckCount: number;
  isSaving: boolean;
  onConfirm: () => void;
}

export function ConfirmAcknowledgeChecklistDialog({
  open,
  onOpenChange,
  checklistName,
  failedCheckCount,
  isSaving,
  onConfirm,
}: ConfirmAcknowledgeChecklistDialogProps) {
  // Don't let Escape / overlay / the X dismiss the dialog mid-save.
  const handleOpenChange = (next: boolean) => {
    if (!next && isSaving) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="p-0 sm:max-w-[576px]" showCloseButton={false}>
        <DialogTitle className="sr-only">{COPY.title}</DialogTitle>
        <div className="flex h-full w-full flex-col items-start bg-default-background">
          <div className="flex w-full flex-col items-start gap-4 overflow-auto px-8 py-6">
            <div className="flex w-full items-center justify-between py-2">
              <span
                className="text-heading-2 font-heading-2 text-primary-navy-800"
                aria-hidden="true"
              >
                {COPY.title}
              </span>
              <IconButton
                variant="neutral-tertiary"
                icon={<FeatherX />}
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              />
            </div>

            <div className="flex w-full flex-col items-start gap-2 rounded-sm bg-neutral-50 px-4 py-4">
              <div className="flex w-full items-start justify-between gap-4">
                <span className="text-caption font-caption text-subtext-color">
                  Checklist
                </span>
                <span className="text-caption font-caption text-default-font text-right">
                  {checklistName}
                </span>
              </div>
              <div className="flex w-full items-start justify-between gap-4">
                <span className="text-caption font-caption text-subtext-color">
                  Failed checks
                </span>
                <span className="text-caption font-caption text-default-font text-right">
                  {failedCheckCount}
                </span>
              </div>
            </div>

            <Alert
              variant="warning"
              title={COPY.alertTitle}
              description={COPY.alertDescription}
            />

            <div className="flex w-full items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                size="large"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                {COPY.cancel}
              </Button>
              <Button
                variant="brand-primary"
                size="large"
                onClick={onConfirm}
                disabled={isSaving}
              >
                {isSaving ? COPY.confirming : COPY.confirm}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
